import type { ComponentType } from 'react';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../hooks/useCustomer';
import { useLiffSession } from '../hooks/useLiff';
import { apiCall } from '../lib/api';
import {
  IdCardIcon, CrownIcon, MapPinIcon,
  PhoneIcon, EnvelopeIcon, CakeIcon, GenderIcon,
  UserIcon, StarIcon, CodeIcon, TagIcon, SignOutIcon,
  HashtagIcon,
} from '../components/icons';
import { BUDGET_OPTIONS, type Customer } from '@meso/types';

type IconComp = ComponentType<{ className?: string; size?: number }>;

interface Row {
  icon: IconComp;
  label: string;
  value: string | null | undefined;
}

interface Section {
  title: string;
  icon: IconComp;
  headerClass: string;
  rowIconClass: string;
  rows: Row[];
}

export function Profile() {
  const { data: customer } = useMe();
  const { logout } = useLiffSession();
  if (!customer) return null;

  const genderLabel = { W: 'หญิง', M: 'ชาย', N: 'ไม่ระบุ' }[customer.gender];
  const budgetLabel = (customer as any).budget_range
    ? BUDGET_OPTIONS.find((b) => b.value === (customer as any).budget_range)?.label ?? null
    : null;

  const sections: Section[] = [
    {
      title: 'ข้อมูลส่วนตัว',
      icon: IdCardIcon,
      headerClass: 'bg-blue-50 text-blue-700',
      rowIconClass: 'bg-blue-50 text-blue-600',
      rows: [
        { icon: UserIcon,     label: 'ชื่อ-นามสกุล', value: `${customer.first_name} ${customer.last_name}`.trim() },
        { icon: GenderIcon,   label: 'เพศ',          value: genderLabel },
        { icon: CakeIcon,     label: 'วันเกิด',      value: customer.birthday },
        { icon: PhoneIcon,    label: 'เบอร์โทร',     value: customer.phone },
        { icon: EnvelopeIcon, label: 'อีเมล',        value: customer.email },
        { icon: HashtagIcon,  label: 'งบประมาณรักษาฝ้า', value: budgetLabel },
      ],
    },
    {
      title: 'สมาชิก & Loyalty',
      icon: CrownIcon,
      headerClass: 'bg-amber-50 text-amber-700',
      rowIconClass: 'bg-amber-50 text-amber-600',
      rows: [
        { icon: CrownIcon, label: 'ระดับสมาชิก', value: customer.tier },
        { icon: StarIcon,  label: 'คะแนน',        value: customer.points.toLocaleString('th-TH') },
        { icon: CodeIcon,  label: 'รหัสแนะนำ',   value: customer.referral_code },
        { icon: TagIcon,   label: 'Shopify ID',  value: customer.shopify_customer_id },
      ],
    },
  ];

  return (
    <div className="max-w-md mx-auto px-4 py-6 space-y-3">
      <h1 className="text-xl font-bold">โปรไฟล์</h1>

      {/* Hero */}
      <div className="card bg-gradient-to-br from-gray-800 to-black text-white">
        <div className="flex items-center gap-3">
          {customer.line_picture_url && (
            <img src={customer.line_picture_url}
                 className="w-16 h-16 rounded-full border-2 border-white/30" alt="" />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-lg truncate">{customer.line_display_name}</div>
            <div className="text-xs text-white/60 truncate">
              {`${customer.first_name} ${customer.last_name}`.trim() || '—'}
            </div>
            <div className="text-[11px] text-white/50 mt-1">
              สมาชิก mesoestetic · {customer.tier}
            </div>
          </div>
        </div>
      </div>

      {sections.map((sec) => {
        const SectionIcon = sec.icon;
        return (
          <div key={sec.title} className="card">
            <div className={`flex items-center gap-2 -mx-4 -mt-4 mb-3 px-4 py-2.5 rounded-t-2xl ${sec.headerClass}`}>
              <SectionIcon className="w-5 h-5" />
              <span className="font-semibold text-sm">{sec.title}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {sec.rows.map((row) => {
                const RowIcon = row.icon;
                return (
                  <div key={row.label} className="flex items-center gap-3 py-2.5">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${sec.rowIconClass}`}>
                      <RowIcon className="w-4 h-4" />
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0">{row.label}</span>
                    <span className="ml-auto font-semibold text-sm text-right break-all">
                      {row.value || '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <ShippingAddressCard customer={customer} />

      <button onClick={logout}
              className="btn btn-block text-red-600 border border-red-300 flex items-center justify-center gap-2">
        <SignOutIcon className="w-4 h-4" />
        ออกจากระบบ
      </button>
    </div>
  );
}

// ─── Shipping address editor ────────────────────────────────────
interface ShipForm {
  ship_address1: string;
  ship_address2: string;
  ship_district: string;
  ship_amphur: string;
  ship_province: string;
  ship_postcode: string;
}

function ShippingAddressCard({ customer }: { customer: Customer }) {
  const qc = useQueryClient();
  const s = customer.shipping ?? {} as any;
  const initial: ShipForm = {
    ship_address1: s.address1 ?? '',
    ship_address2: s.address2 ?? '',
    ship_district: s.district ?? '',
    ship_amphur:   s.amphur ?? '',
    ship_province: s.province ?? '',
    ship_postcode: s.postcode ?? '',
  };
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ShipForm>(initial);
  const filled = Object.values(initial).some(Boolean);

  const save = useMutation({
    mutationFn: async () => {
      // Postcode optional but if present must be 5 digits
      const payload: any = { ...form };
      if (!payload.ship_postcode) delete payload.ship_postcode;
      else if (!/^\d{5}$/.test(payload.ship_postcode)) {
        throw new Error('รหัสไปรษณีย์ต้องเป็น 5 หลัก');
      }
      return apiCall('patch', '/customers/me', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      setEditing(false);
    },
  });

  if (!editing) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 -mx-4 -mt-4 mb-3 px-4 py-2.5 rounded-t-2xl bg-rose-50 text-rose-700">
          <MapPinIcon className="w-5 h-5" />
          <span className="font-semibold text-sm flex-1">ที่อยู่จัดส่ง</span>
          <button onClick={() => setEditing(true)}
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white text-rose-700 border border-rose-200">
            {filled ? 'แก้ไข' : 'เพิ่มที่อยู่'}
          </button>
        </div>
        {filled ? (
          <div className="text-sm leading-relaxed">
            {initial.ship_address1}{initial.ship_address2 && ` ${initial.ship_address2}`}
            <br />
            {[initial.ship_district, initial.ship_amphur, initial.ship_province, initial.ship_postcode]
              .filter(Boolean).join(' · ')}
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            ยังไม่มีที่อยู่จัดส่ง — กดเพิ่มเพื่อให้ระบบจัดส่งสินค้าไปถึงคุณได้
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center gap-2 -mx-4 -mt-4 mb-3 px-4 py-2.5 rounded-t-2xl bg-rose-50 text-rose-700">
        <MapPinIcon className="w-5 h-5" />
        <span className="font-semibold text-sm flex-1">ที่อยู่จัดส่ง</span>
      </div>

      <div className="space-y-3">
        <div>
          <label className="form-label">บ้านเลขที่ / ถนน *</label>
          <input className="form-input" value={form.ship_address1}
                 onChange={(e) => setForm({ ...form, ship_address1: e.target.value })} />
        </div>
        <div>
          <label className="form-label">รายละเอียดเพิ่มเติม</label>
          <input className="form-input" value={form.ship_address2}
                 placeholder="หมู่บ้าน / ตึก / ชั้น (ไม่บังคับ)"
                 onChange={(e) => setForm({ ...form, ship_address2: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="form-label">ตำบล/แขวง</label>
            <input className="form-input" value={form.ship_district}
                   onChange={(e) => setForm({ ...form, ship_district: e.target.value })} />
          </div>
          <div>
            <label className="form-label">อำเภอ/เขต</label>
            <input className="form-input" value={form.ship_amphur}
                   onChange={(e) => setForm({ ...form, ship_amphur: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="form-label">จังหวัด *</label>
            <input className="form-input" value={form.ship_province}
                   onChange={(e) => setForm({ ...form, ship_province: e.target.value })} />
          </div>
          <div>
            <label className="form-label">รหัสไปรษณีย์</label>
            <input className="form-input" inputMode="numeric" maxLength={5}
                   value={form.ship_postcode}
                   onChange={(e) => setForm({ ...form, ship_postcode: e.target.value })} />
          </div>
        </div>

        {save.isError && (
          <div className="form-error text-center">{(save.error as Error).message}</div>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={() => { setForm(initial); setEditing(false); }}
                  className="btn flex-1 border border-gray-300">ยกเลิก</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !form.ship_address1 || !form.ship_province}
                  className="btn btn-primary flex-1">
            {save.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}
